import {
    ClessonResults,
    ProblemResults,
    LessonProblem,
    StudentProblemResult,
    StudentResults,
} from '@yandex-int/k-common/typings';
import isEmpty from 'lodash/isEmpty';
import { getStudentsLessonsCertificates } from 'platform-components/src/components/awards/selectors';
import { getCertificatesFromAwards } from 'platform-components/src/components/awards/utils';
import getStudentName from 'platform-components/src/utils/get-student-name';
import { createSelector } from 'reselect';

import { getStudents } from 'common.components/classes/selectors';
import * as lessonSelectors from 'common.components/lesson/selectors';
import { getProblemMap, isDictation } from 'store/problems/utils';
import {
    filterSeenStacks,
    filterSuccessStacks,
    filterTheoryStacks,
    getLastAnsweredOrFirstProblemLink,
} from 'store/skills-lesson-problems/utils';
import { getClessonStudentsStickers } from 'store/stickers/selectors';
import { getProblemIdFromProps, getStudentIdFromProps } from 'utils/selectors/selectors';

import { CurrentCellData, LessonResultsStore, StudentProblemLinkGetter, StudentsResult } from './typings';

const lessonResultsStoreSelector = (storeState: any): LessonResultsStore => storeState.lessonResults;

export const getShouldShowAttemptsValue = createSelector(
    [lessonResultsStoreSelector],
    (lessonResults: LessonResultsStore): boolean => lessonResults.shouldShowAttempts
);

export const getShouldShowTimeValue = createSelector(
    [lessonResultsStoreSelector],
    (lessonResults: LessonResultsStore): boolean => lessonResults.shouldShowTime
);

export const getShouldShowPointsValue = createSelector(
    [lessonResultsStoreSelector],
    (lessonResults: LessonResultsStore): boolean => lessonResults.shouldShowPoints
);

export const getStudentsResult = createSelector(
    [getStudents, lessonSelectors.getClessonResults, lessonSelectors.getEvaluableProblemsNumber],
    (students, clessonResults, evaluableProblemsNumber): Array<StudentsResult> => {
        // фильтруем учеников, которым занятие не выдавали
        return (
            students?.reduce((acc: Array<StudentsResult>, student) => {
                const result = clessonResults?.students[student.id];
                const studentProblems = result?.problems;

                if (studentProblems && !isEmpty(studentProblems) && result) {
                    const time = result.time;

                    acc.push({
                        studentId: student.id,
                        studentName: getStudentName(student.firstName, student.lastName),
                        result: {
                            problems: result.problems,
                            overall: {
                                seenPercent: Math.round((result.seenNumber / result.problemsNumber) * 100),
                                correctPercent: Math.round((result.correctNumber / evaluableProblemsNumber) * 100),
                                time: time,
                                points: result.points,
                                maxPoints: result.maxPoints,
                            },
                        },
                    });
                }

                return acc;
            }, []) || []
        );
    }
);

export const getStudentsCustomFlowLessonResult = createSelector(
    [
        getStudents,
        lessonSelectors.getLesson,
        lessonSelectors.getMainStacksFromLessonStructure,
        lessonSelectors.getClessonResults,
    ],
    // eslint-disable-next-line max-params
    (students, lesson, mainStacks, clessonResults): StudentsResult[] => {
        if (!lesson || !clessonResults) {
            return [];
        }

        const problemMap = getProblemMap(lesson);

        return (
            students?.reduce((acc: StudentsResult[], student) => {
                const studentId = student.id;
                const result: StudentResults | undefined = clessonResults?.students[studentId];
                const studentProblemResult = result?.problems;

                if (!studentProblemResult || isEmpty(studentProblemResult) || !result) {
                    return acc;
                }

                const theoryStacks = filterTheoryStacks(mainStacks, problemMap);
                const seenStacks = filterSeenStacks(mainStacks, problemMap, studentProblemResult);
                const successStacks = filterSuccessStacks(mainStacks, problemMap, studentProblemResult);

                const time = result.time;

                const evaluableStacks = mainStacks.length - theoryStacks.length;

                const seenPercent = Math.round((seenStacks.length / mainStacks.length) * 100);
                const correctPercent = Math.round((successStacks.length / evaluableStacks) * 100);

                acc.push({
                    studentId: student.id,
                    studentName: getStudentName(student.firstName, student.lastName),
                    result: {
                        problems: result.problems,
                        overall: {
                            seenPercent,
                            correctPercent,
                            time: time,
                            points: result.points,
                            maxPoints: result.maxPoints,
                        },
                    },
                });

                return acc;
            }, []) || []
        );
    }
);

export const getStudentsCustomFlowLessonProblemResultGetter = createSelector(
    [lessonSelectors.getLesson, lessonSelectors.getMainStacksFromLessonStructure, lessonSelectors.getClessonResults],
    // eslint-disable-next-line max-params
    (lesson, mainStacks, clessonResults) => {
        if (!lesson) {
            return null;
        }
        const problemMap = getProblemMap(lesson);
        return (studentId: number, columnIndex: number) => {
            const result = clessonResults?.students[studentId];
            const studentProblemResult = result?.problems;
            const stack = mainStacks[columnIndex];
            if (!studentProblemResult || isEmpty(studentProblemResult) || !result) {
                return null;
            }

            return getLastAnsweredOrFirstProblemLink(stack, problemMap, studentProblemResult);
        };
    }
);

export const getProblemsResult = createSelector(
    [lessonSelectors.getClessonResults],
    (clessonResults: ClessonResults | null): Record<number, ProblemResults> | null => clessonResults?.problems || null
);

export const getShouldShowDescriptionPopup = createSelector(
    [lessonResultsStoreSelector],
    (state: LessonResultsStore): boolean => state.shouldShowDescriptionPopup
);

export const getDescriptionPopupData = createSelector(
    [lessonResultsStoreSelector],
    (state: LessonResultsStore): CurrentCellData | null => state.cellDescriptionData
);

export const getStudentResultByIds = createSelector(
    [getStudentsResult, getStudentIdFromProps, getProblemIdFromProps],
    (students: Array<StudentsResult>, studentId, problemId): StudentProblemResult | null => {
        if (!studentId || !problemId) {
            return null;
        }

        const studentResult = students.find(({ studentId: id }: StudentsResult) => id === studentId);
        return studentResult?.result.problems[problemId] || null;
    }
);

export const getStudentNameById = createSelector(
    [getStudents, getStudentIdFromProps],
    (students, studentId): string | null => {
        if (!studentId || !students) {
            return null;
        }

        const student = students.find((element) => element.id === studentId);
        return getStudentName(student?.firstName, student?.lastName);
    }
);

// карточки занятия, которые могут отображаться в общей статистике
export const getCommonProblems = createSelector(
    [lessonSelectors.getLessonProblems, lessonSelectors.getCustomFlowLessonProblems],
    (lessonProblems: Array<LessonProblem>, customFlowLessonProblems): Array<LessonProblem> => {
        if (customFlowLessonProblems && customFlowLessonProblems.length) {
            return customFlowLessonProblems;
        }

        return lessonProblems.filter((pl) => pl.problem && !isDictation(pl.problem));
    }
);

export const getDictationProblems = createSelector(
    [lessonSelectors.getLessonProblems],
    (lessonProblems: Array<LessonProblem>): Array<LessonProblem> => {
        return lessonProblems.filter((pl) => pl.problem && isDictation(pl.problem));
    }
);

export const getMaxPoints = createSelector([getStudentsResult], (studentsResults: Array<StudentsResult>): number => {
    if (!studentsResults.length) {
        return 0;
    }
    return studentsResults[0].result.overall.maxPoints;
});

export const getIsStatsExpanded = createSelector(
    [lessonResultsStoreSelector],
    (state): boolean => state.isStatsExpanded
);

export const getStudentsOnline = createSelector(
    [lessonResultsStoreSelector],
    (state): Record<number, number | null> => state.studentsOnline
);

export const getStudentsWithoutStickers = createSelector(
    [getStudentsResult, getClessonStudentsStickers],
    (results, stickers) => {
        if (!results) {
            return null;
        }
        return results.filter((result) => !stickers[result.studentId]);
    }
);

export const getStudentIdsWithoutStickers = createSelector([getStudentsWithoutStickers], (results) => {
    if (!results) {
        return null;
    }
    return results.map((result) => result.studentId);
});

export const getIsEveryoneHasStickers = createSelector(
    [getStudentsWithoutStickers],
    (studentsWithoutStickers): boolean => {
        if (!studentsWithoutStickers) {
            return false;
        }
        return studentsWithoutStickers.length === 0;
    }
);

export const getResultsViewProps = (storeState: any, { classId }: { classId: number | null }) => {
    const isCustomFlowLesson = lessonSelectors.getIsCustomFlowLesson(storeState);
    let studentsResult: StudentsResult[];
    let getStudentProblemLink: StudentProblemLinkGetter | null = null;
    if (isCustomFlowLesson) {
        getStudentProblemLink = getStudentsCustomFlowLessonProblemResultGetter(storeState);
        studentsResult = getStudentsCustomFlowLessonResult(storeState, { classId });
    } else {
        studentsResult = getStudentsResult(storeState, { classId });
    }

    return {
        getStudentProblemLink,
        studentsResult,
    };
};

// карточки занятия, которые могут отображаться в общей статистике +
// + если карточка из занятия по навыкам, то ищем последнее решенное обучение
export const getStudentCommonProblems = createSelector(
    [getStudentIdFromProps, getCommonProblems, getResultsViewProps],
    (studentId, problems, { getStudentProblemLink }) =>
        problems.map((lessonProblem, problemIndex) => {
            let lp = lessonProblem;
            if (getStudentProblemLink && studentId) {
                lp = getStudentProblemLink(studentId, problemIndex) || lessonProblem;
            }
            return lp;
        })
);

export const getCertificatesByStudentId = createSelector([getStudentsLessonsCertificates], (awardItems) =>
    getCertificatesFromAwards(awardItems)
);
